import type { ReactNode } from 'react'

/**
 * A mark that says what it means. The seat list used to spell every state out -
 * "executed", "term-limited", "offline" - which was unambiguous and wrapped
 * onto three lines the moment a seat held two of them at once. The words are
 * now icons, and this is what gives them back their names.
 *
 * Hover is not the only way in: the span is focusable, so the bubble appears on
 * keyboard focus too, and the label is the accessible name either way. That
 * matters because the one place these are never hovered is the table screen,
 * where a screen reader or a curious phone are the only things that will ask.
 *
 * Positioned in CSS rather than measured in JS. A seat row is never near enough
 * to the viewport edge for a bubble to be clipped, and a resize observer for
 * six small labels is a poor trade.
 */
export function Mark({
  label,
  className = '',
  children,
}: {
  /** The word this mark replaced. Becomes both the bubble and the aria-label. */
  label: string
  className?: string
  children: ReactNode
}) {
  return (
    <span className={`mark ${className}`} tabIndex={0} role="img" aria-label={label}>
      {children}
      <span className="mark__bubble" aria-hidden="true">
        {label}
      </span>
    </span>
  )
}
